import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { readCachedRole } from "../lib/sessionFlag.js";

// Cmd+K (Mac) / Ctrl+K (everything else) jump-to for staff. Navigation only:
// each destination still runs its own auth check, so the cached role here is
// UX steering exactly like the Navbar's, not a security boundary.
const DESTINATIONS = [
  { label: "Staff dashboard", hint: "/staff/dashboard", to: "/staff/dashboard", keys: "home overview" },
  { label: "User console", hint: "/staff/console", to: "/staff/console", keys: "users people accounts search hacker applicant" },
  { label: "Data browser", hint: "/staff/console?mode=data", to: "/staff/console?mode=data", keys: "database collections raw documents" },
  { label: "QR scanner", hint: "/staff/scanner", to: "/staff/scanner", keys: "check-in checkin scan attendance meals shirts" },
  { label: "Metrics", hint: "/staff/metrics", to: "/staff/metrics", keys: "stats attendance attrition numbers charts" },
  { label: "Outbox", hint: "/staff/outbox", to: "/staff/outbox", keys: "email campaigns send chase rsvp reminder" },
  { label: "CSV export", hint: "/csv", to: "/csv", keys: "csv export download spreadsheet sheet list track checked in rsvp" },
  { label: "Reimbursements", hint: "/staff/reimbursements", to: "/staff/reimbursements", keys: "travel money treasurer" },
  { label: "Review applications", hint: "/review", to: "/review", keys: "applicants accept reject waitlist decisions" },
  { label: "Judging: Judge", hint: "/judging", to: "/judging", keys: "score walk form" },
  { label: "Judging: Leaderboard", hint: "/judging?view=leaderboard", to: "/judging?view=leaderboard", keys: "rankings results winners standings" },
  { label: "Judging: Operations", hint: "/judging?view=ops", to: "/judging?view=ops", keys: "ops tracks tables judges coverage" },
  { label: "Judging: Teams", hint: "/judging?view=teams", to: "/judging?view=teams", keys: "team roster merge unassigned matchmaking" },
  { label: "Tracks", hint: "/tracks", to: "/tracks", keys: "prizes sponsors categories" },
  { label: "Schedule", hint: "/schedule", to: "/schedule", keys: "events times agenda" },
];

const isMac = () =>
  typeof navigator !== "undefined" &&
  /mac|iphone|ipad/i.test(navigator.userAgentData?.platform || navigator.platform || "");

function matches(query) {
  const q = query.trim().toLowerCase();
  if (!q) return DESTINATIONS;
  const words = q.split(/\s+/);
  return DESTINATIONS
    .map((d) => {
      const hay = `${d.label} ${d.hint} ${d.keys}`.toLowerCase();
      if (!words.every((w) => hay.includes(w))) return null;
      // Label-prefix hits sort first so "out" lands on Outbox, not a keyword.
      return { d, rank: d.label.toLowerCase().startsWith(q) ? 0 : 1 };
    })
    .filter(Boolean)
    .sort((a, b) => a.rank - b.rank)
    .map((m) => m.d);
}

export default function CommandPalette() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [cursor, setCursor] = useState(0);
  const inputRef = useRef(null);

  const close = useCallback(() => {
    setOpen(false);
    setQuery("");
    setCursor(0);
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      const isK = e.key === "k" || e.key === "K";
      if (isK && (e.metaKey || e.ctrlKey) && !e.altKey && !e.shiftKey) {
        // Cmd+K on Mac; Ctrl+K on Windows/Linux (where it would otherwise
        // focus the browser's search bar). Ctrl+K on a Mac opens it too.
        // Role is read at press time, not mount: the cache is written after
        // sign-in resolves, which is usually after this component mounted.
        if (readCachedRole() !== "staff") return;
        e.preventDefault();
        setOpen((o) => !o);
        return;
      }
      if (e.key === "Escape" && open) {
        e.preventDefault();
        close();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, close]);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  const results = useMemo(() => matches(query), [query]);
  const trimmed = query.trim();
  // Anything typed can fall through to the console's people search, which
  // already handles names and emails, so the palette covers "find a hacker"
  // without reading Firestore itself.
  const rows = useMemo(
    () =>
      trimmed
        ? [...results, { label: `Search users for "${trimmed}"`, hint: "/staff/console", to: `/staff/console?q=${encodeURIComponent(trimmed)}`, search: true }]
        : results,
    [results, trimmed]
  );

  useEffect(() => {
    setCursor(0);
  }, [query]);

  const go = (row) => {
    close();
    navigate(row.to);
  };

  const onInputKey = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setCursor((c) => Math.min(c + 1, rows.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setCursor((c) => Math.max(c - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (rows[cursor]) go(rows[cursor]);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[9980] flex items-start justify-center p-4 pt-[12vh]">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={close} aria-hidden="true" />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Jump to a page"
        className="relative w-full max-w-lg rounded-xl border border-white/15 bg-[#0d1130] shadow-2xl overflow-hidden"
      >
        <div className="flex items-center gap-3 px-4 border-b border-white/10">
          <span className="text-gray-500 text-sm" aria-hidden="true">›</span>
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onInputKey}
            placeholder="Jump to a page, or type a name or email"
            aria-label="Jump to a page, or search users"
            aria-activedescendant={rows[cursor] ? `cmdk-${cursor}` : undefined}
            className="flex-1 bg-transparent py-3.5 text-sm text-white placeholder-gray-500 focus:outline-none"
          />
          <kbd className="text-[10px] font-mono text-gray-500 border border-white/10 rounded px-1.5 py-0.5">esc</kbd>
        </div>
        <ul role="listbox" className="max-h-[60vh] overflow-y-auto py-1.5">
          {rows.map((row, i) => (
            <li
              key={row.to}
              id={`cmdk-${i}`}
              role="option"
              aria-selected={i === cursor}
              onMouseEnter={() => setCursor(i)}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => go(row)}
              className={`flex items-center justify-between gap-4 px-4 py-2 text-sm cursor-pointer ${
                i === cursor ? "bg-cyan-500/15 text-white" : "text-gray-300"
              } ${row.search ? "border-t border-white/10 mt-1.5 pt-3" : ""}`}
            >
              <span className={row.search ? "text-cyan-300" : ""}>{row.label}</span>
              <span className="text-[11px] font-mono text-gray-500 truncate">{row.hint}</span>
            </li>
          ))}
          {rows.length === 0 && (
            <li className="px-4 py-6 text-sm text-gray-500 text-center">No matches.</li>
          )}
        </ul>
        <div className="px-4 py-2 border-t border-white/10 text-[10px] text-gray-500 flex gap-4">
          <span><kbd className="font-mono">↑↓</kbd> move</span>
          <span><kbd className="font-mono">↵</kbd> open</span>
          <span className="ml-auto"><kbd className="font-mono">{isMac() ? "⌘K" : "Ctrl+K"}</kbd> toggle</span>
        </div>
      </div>
    </div>
  );
}
